import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';

import styles from './Home.module.css';

import { Issue, NAVBAR_ICON_URL } from '../../../constants';
import { AppContext } from '../../../App.context';

import { HomeContext, SortType } from './Home.hooks';

import { TrackingContext } from '../../../tracking/Tracking.context';
import { Loading } from '../../../components/loading/Loading';

import bannerAdImg from '../images/ban-mobile.jpg';
import bannerAdDesktopImg from '../images/ban-desktop.jpg';
import { useIssues } from '../../../hooks/use-issue';
import { IS_AUTHENTICATED_KEY } from '@/auth/constants';

export const Home = ({ onClickLogOut }: HomeProps) => {
	const isAuthenticated = localStorage.getItem(IS_AUTHENTICATED_KEY);
	const [search, setSearch] = useState('');
	const [sortBy, setSortBy] = useState<SortType>('date-desc');
	const { issues, isLoading } = useIssues();

	if (!isAuthenticated) {
		return (
			<div className={styles['home']}>
				<div className={styles['view']}>
					<div className={styles['loading']}>
						<Loading />
					</div>
				</div>
			</div>
		);
	}

	return (
		<HomeContext.Provider
			value={{
				issues: issues
					.filter(filterSearch(search))
					.sort(sortIssues(sortBy)),
				isLoading,
				search,
				setSearch,
				sortBy,
				setSortBy,
				onClickLogOut,
			}}
		>
			<div className={styles['home']}>
				<div className={styles['view']}>
					<Topbar />
					<Content />
				</div>
			</div>
		</HomeContext.Provider>
	);
};

interface HomeProps {
	onClickLogOut?: (url?: string) => void;
}

function filterSearch(search: string) {
	return (issue: Issue): boolean => {
		const { name } = issue;
		return name.toLowerCase().includes(search.toLowerCase());
	};
}

function sortIssues(sortType: SortType) {
	return (a: Issue, b: Issue) => {
		switch (sortType) {
			case 'alpha-asc':
				if (a.name < b.name) {
					return -1;
				}
				if (a.name > b.name) {
					return 1;
				}
				return 0;
			case 'alpha-desc':
				if (a.name > b.name) {
					return -1;
				}
				if (a.name < b.name) {
					return 1;
				}
				return 0;
			case 'date-asc':
				return (
					new Date(`${a.releaseDate}`).getTime() -
					new Date(`${b.releaseDate}`).getTime()
				);
			default:
				return (
					new Date(`${b.releaseDate}`).getTime() -
					new Date(`${a.releaseDate}`).getTime()
				);
		}
	};
}

const Topbar = () => {
	// TODO Implement logout button
	const { onClickLogOut } = useContext(HomeContext);
	return (
		<div className={styles['topbar']}>
			<div />
			<div>
				<img src={NAVBAR_ICON_URL} alt="Logo" />
			</div>

			<div>
				{onClickLogOut ? (
					<button onClick={onClickLogOut}>Log out</button>
				) : null}
			</div>
		</div>
	);
};

const Content = () => {
	const { isLoading } = useContext(HomeContext);
	if (isLoading) {
		return (
			<div className={styles['loading']}>
				<Loading />
			</div>
		);
	}

	return (
		<div className={styles['content']}>
			<Banner />
			<h2>Library</h2>
			<FilterBar />
			<Issues />
		</div>
	);
};

const Banner = () => {
	const { device } = useContext(AppContext);
	if (!device) {
		return null;
	}
	return (
		<div className={styles['banner']}>
			<a
				href="https://magazineshop.us/collections/all"
				rel="noreferrer"
				target={'_blank'}
			>
				<img
					src={
						`${device.type}` === 'desktop'
							? bannerAdDesktopImg
							: bannerAdImg
					}
					alt={`${device.type}`}
				/>
			</a>
		</div>
	);
};

const FilterBar = () => {
	return (
		<div className={styles['filter-bar']}>
			<div>
				<SortBar />
			</div>
			<div>
				<SearchBar />
			</div>
		</div>
	);
};

const SortBar = () => {
	const { sortBy, setSortBy } = useContext(HomeContext);
	return (
		<select
			title={'Sort'}
			value={sortBy}
			onChange={e => setSortBy(e.target.value as SortType)}
		>
			<option disabled>Sort</option>
			<option value={'date-desc'}>Date, new to old</option>
			<option value={'date-asc'}>Date, old to new</option>
			<option value={'alpha-desc'}>Alphabetically, Z-A</option>
			<option value={'alpha-asc'}>Alphabetically, A-Z</option>
		</select>
	);
};

const SearchBar = () => {
	const { search, setSearch } = useContext(HomeContext);
	return (
		<input
			type="search"
			placeholder="Search"
			value={search}
			onChange={e => setSearch(e.target.value)}
		/>
	);
};

const Issues = () => {
	const { issues } = useContext(HomeContext);
	return (
		<div className={styles['issues']}>
			{issues.map((i: Issue) => (
				<IssueComponent key={i.id} issue={i} />
			))}
		</div>
	);
};

const IssueComponent = ({ issue }: IssueProps) => {
	const { sendEvent } = useContext(TrackingContext);
	const { id, name, cover, slug } = issue;
	const url = `/issues/${slug ? slug : id}`;
	const from = `${window.location.pathname}`;
	return (
		<Link
			to={url}
			state={{ from }}
			onClick={() => {
				sendEvent({
					type: 'BTN_CLICK_ISSUE',
					data: {
						issue: {
							id: parseInt(`${issue.id}`, 10),
							slug: issue.slug,
						},
					},
				});
			}}
		>
			<figure style={{ cursor: 'pointer' }} className={styles['issue']}>
				<img src={cover} alt={name} />
				<figcaption>{name}</figcaption>
			</figure>
		</Link>
	);
};

interface IssueProps {
	issue: Issue;
}
